import { useCallback, useEffect } from "react";
import {
  getNextStepAfterAddress,
  getPreviousStepBeforeAffiliation,
  isPostPaymentAbstractEnabled,
  POST_PAYMENT_ABSTRACT_FORM_STEP,
  shouldSkipInlineAbstractStep,
} from "../registrationModules";

export const useRegistrationNavigation = ({
  step,
  setStep,
  totalSteps,
  abstractStepNumber,
  abstractMetadataLoaded,
  validateCurrentStep,
  formData,
  isPaymentBlockedPendingPaperApproval,
  paymentBlockedPaperStatusMessage,
  agreedToTerms,
  setTermsError,
  scrollToTermsCheckbox,
  showPaperSubmissionSummary,
  setShowPaperSubmissionSummary,
  navigationDecisionRef,
  navigationDecisionVersion,
  conferenceDataLoadedRef,
  isPendingPaperApprovalStatus,
  isApprovalMandateEnabled,
  isPaperNavigationLocked,
  setIsInitialLoad,
}) => {
  useEffect(() => {
    if (step === 3 && shouldSkipInlineAbstractStep(abstractStepNumber)) {
      setStep(4);
    }
  }, [step, abstractStepNumber, setStep]);

  useEffect(() => {
    if (
      navigationDecisionRef.current.ready &&
      conferenceDataLoadedRef.current
    ) {
      if (!abstractMetadataLoaded) {
        console.log(
          "🎯 Navigation decision waiting for abstract metadata before routing"
        );
        return;
      }

      const { isPaid, hasPaperSubmitted, paperStatus } =
        navigationDecisionRef.current;

      console.log("🎯 Navigation useEffect triggered:");
      console.log("  - abstractStepNumber:", abstractStepNumber);
      console.log("  - isPaid:", isPaid);
      console.log("  - hasPaperSubmitted:", hasPaperSubmitted);
      console.log("  - paperStatus:", paperStatus);

      const isPaperPendingApproval =
        isApprovalMandateEnabled &&
        hasPaperSubmitted &&
        (isPendingPaperApprovalStatus(paperStatus) || !paperStatus);
      const normalizedPaperStatus = (paperStatus || "").trim().toUpperCase();
      const canResumePaper =
        normalizedPaperStatus === "DRAFT" ||
        normalizedPaperStatus === "MINOR_REVISION" ||
        normalizedPaperStatus === "MAJOR_REVISION" ||
        normalizedPaperStatus === "REVISION_REQUESTED";
      const shouldOpenPostPaymentPaperStep =
        isPostPaymentAbstractEnabled(abstractStepNumber) &&
        ((isPaid && !hasPaperSubmitted) || canResumePaper);

      if (isPaperPendingApproval) {
        console.log(
          "🎯 Navigation useEffect: paper pending approval - routing to step 3 paper summary"
        );
        setShowPaperSubmissionSummary(true);
        setStep(3);
        navigationDecisionRef.current.ready = false;
        setIsInitialLoad?.(false);
        return;
      }

      if (shouldOpenPostPaymentPaperStep) {
        console.log("🎯 Navigating to post-payment abstract submission step");
        setStep(POST_PAYMENT_ABSTRACT_FORM_STEP);
      } else {
        console.log("🎯 Navigating to step 6 - Summary page");
        setStep(6);
      }

      navigationDecisionRef.current.ready = false;
      setIsInitialLoad?.(false);
    }
  }, [
    abstractStepNumber,
    abstractMetadataLoaded,
    conferenceDataLoadedRef,
    isPendingPaperApprovalStatus,
    isApprovalMandateEnabled,
    navigationDecisionRef,
    navigationDecisionVersion,
    setIsInitialLoad,
    setShowPaperSubmissionSummary,
    setStep,
  ]);

  const next = useCallback(() => {
    if (!validateCurrentStep(step)) {
      return;
    }

    if (step === 2) {
      const nextStep = getNextStepAfterAddress(abstractStepNumber);
      if (nextStep !== 3) {
        setStep(nextStep);
        return;
      }
    }

    if (step === 6) {
      if (!formData.id && !formData.regId) {
        alert(
          "Please save your registration before proceeding to payment. Click the 'Save' button first."
        );
        return;
      }

      if (isPaymentBlockedPendingPaperApproval) {
        alert(paymentBlockedPaperStatusMessage);
        return;
      }

      if (!agreedToTerms) {
        setTermsError(
          "You must agree to the Terms & Conditions before proceeding to payment."
        );
        scrollToTermsCheckbox();
        return;
      }
    }

    setStep((currentStep) => Math.min(currentStep + 1, totalSteps));
  }, [
    abstractStepNumber,
    agreedToTerms,
    formData.id,
    formData.regId,
    isPaymentBlockedPendingPaperApproval,
    paymentBlockedPaperStatusMessage,
    scrollToTermsCheckbox,
    setStep,
    setTermsError,
    step,
    totalSteps,
    validateCurrentStep,
  ]);

  const prev = useCallback(() => {
    if (isPaperNavigationLocked) {
      alert(paymentBlockedPaperStatusMessage);
      return;
    }

    if (step === 3 && showPaperSubmissionSummary) {
      setShowPaperSubmissionSummary(false);
      setStep(3);
      return;
    }

    if (step === 3) {
      setStep(2);
      return;
    }

    if (step === 4) {
      const previousStep = getPreviousStepBeforeAffiliation(abstractStepNumber);
      if (previousStep !== 3) {
        setStep(previousStep);
        return;
      }
    }

    setStep((currentStep) => Math.max(currentStep - 1, 1));
  }, [
    abstractStepNumber,
    isPaperNavigationLocked,
    paymentBlockedPaperStatusMessage,
    setShowPaperSubmissionSummary,
    setStep,
    showPaperSubmissionSummary,
    step,
  ]);

  return { next, prev };
};
